import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabView, TabPanel } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useMemo, useState } from "react";
import {
  icmRequestStatus,
  initiateIcmRequest,
  catResults,
  icmJobHistory,
  downloadIcmArtifact,
  downloadAllIcmArtifacts,
  initiateRmsRequest,
  rmsRequestStatus,
  rmsJobHistory,
  downloadRmsArtifact,
  downloadAllRmsArtifacts,
} from "../../../api/routes";
import { SafeReportResponse } from "../Conversion";
import styles from "./CatCard.module.css";
import {
  WorkStatus,
  CatResultsResponse,
  CatJobHistoryItem,
} from "../../../client/types.gen";
import { useToast } from "../../../context-providers/Toast";
import { readableLayer } from "../layers/util";
import { downloadBlob } from "../../../util";
import { formatDuration, getPerilColor } from "../icm/util";
import * as client from "../../../client";
import { IfFeatureEnabled, useFeatureEnabled } from "../../../IfFeatureEnabled";
import { useLocalUser } from "../data";
import { formatExactMoneyDollars } from "../Money";

// This could be shared with the backend, but for now I am OK with a looser
// coupling in the hopes we remove this restriction once we are running
// CAT for real submissions.
const RMS_LARGE_LOCATION_THRESHOLD = 1000;

type PerilCode =
  | "PWH"
  | "PSH"
  | "PPH"
  | "PES"
  | "PSL"
  | "PFF"
  | "PFL"
  | "PWB"
  | "PWW"
  | "PWT";

const PERIL_OPTIONS: { label: string; value: PerilCode }[] = [
  { label: "Hurricane - Wind Only", value: "PWH" },
  { label: "Hurricane - Storm Surge", value: "PSH" },
  { label: "Earthquake - Ground Shake", value: "PES" },
  { label: "Earthquake - Sprinkler Leak.", value: "PSL" },
  { label: "Earthquake - Fire Following", value: "PFF" },
  { label: "Inland Flood", value: "PFL" },
  { label: "Wildfire", value: "PWB" },
  { label: "Winter Storm", value: "PWW" },
  { label: "Severe Thunderstorm", value: "PWT" },
];

const PERIL_DISPLAY_NAMES = new Map<string, string>(
  PERIL_OPTIONS.map((o) => [o.value, o.label])
);

// RMS EP Curve constants: we have more of this data, but keeping it limited
// for the time being since the table gets busy.
const RETURN_PERIODS = [100, 250];

// Type extension for RMS EP rows - peril field not yet in generated types
interface RmsEpRowWithPeril extends client.EpRow {
  peril?: string | null;
}

// Enriched RMS layer with matched layer_id from report config
interface RmsLayerResult extends client.CatLayerResult {
  layer_id: string | null;
}

interface CatCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

interface LayerPerilRow {
  layer: string;
  peril: string;
  aal: number | null;
  pml_100: number | null;
  pml_250: number | null;
  pml_1000: number | null;
  isFirstInGroup?: boolean;
}

interface EPTableRow {
  label: string;
  perspective: string;
  metric_type: string;
  values: Record<number, number>;
  isTotalRow?: boolean;
}

export const CatCard: React.FC<CatCardProps> = ({ specialtyPropertyInfo }) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isIcmInitiateLoading, setIsIcmInitiateLoading] = useState(false);
  const [isRmsInitiateLoading, setIsRmsInitiateLoading] = useState(false);
  const [pendingIcmRequestIds, setPendingIcmRequestIds] = useState<string[]>(
    []
  );
  const [pendingRmsRequestIds, setPendingRmsRequestIds] = useState<string[]>(
    []
  );
  const [icmResultsData, setIcmResultsData] =
    useState<CatResultsResponse | null>(null);
  const [rmsResultsData, setRmsResultsData] =
    useState<CatResultsResponse | null>(null);
  const [jobHistory, setJobHistory] = useState<CatJobHistoryItem[]>([]);
  const [viewingBatchId, setViewingBatchId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<"icm" | "rms">("rms");
  const toast = useToast();
  const user = useLocalUser();
  const rmsLargeLocationPermitted = useFeatureEnabled(
    "rms-large-location-permitted",
    undefined,
    user
  );
  const [showLargeLocationWarning, setShowLargeLocationWarning] =
    useState(false);
  const numLocations = specialtyPropertyInfo.precomputed_metadata.num_locations;
  const localUser = useLocalUser();

  // Load existing results and check for pending jobs on mount
  useEffect(() => {
    const loadExistingResults = async () => {
      try {
        const [catRes, icmHist, rmsHist] = await Promise.all([
          catResults(specialtyPropertyInfo.report_id).catch(() => null),
          icmJobHistory(specialtyPropertyInfo.report_id),
          rmsJobHistory(specialtyPropertyInfo.report_id),
        ]);

        if (catRes?.icm?.air?.aggregate?.layers?.length) {
          setIcmResultsData(catRes);
        }

        if (catRes?.rms?.aggregate?.layers?.length) {
          setRmsResultsData(catRes);
        }

        // Set default selected model: prefer RMS if available, otherwise ICM
        if (catRes?.rms?.aggregate?.layers?.length) {
          setSelectedModel("rms");
        } else if (catRes?.icm?.air?.aggregate?.layers?.length) {
          setSelectedModel("icm");
        }

        // Combine job histories
        const allJobs = [...(icmHist.jobs ?? []), ...(rmsHist.jobs ?? [])].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );

        setJobHistory(allJobs);

        // Check for active running jobs
        const activeIcmIds = (icmHist.jobs ?? [])
          .filter(
            (job) =>
              job.status === WorkStatus.PENDING ||
              job.status === WorkStatus.IN_PROGRESS
          )
          .map((job) => job.batch_id);
        const activeRmsIds = (rmsHist.jobs ?? [])
          .filter(
            (job) =>
              job.status === WorkStatus.PENDING ||
              job.status === WorkStatus.IN_PROGRESS
          )
          .map((job) => job.batch_id);

        if (activeIcmIds.length > 0) {
          setPendingIcmRequestIds(activeIcmIds);
        }
        if (activeRmsIds.length > 0) {
          setPendingRmsRequestIds(activeRmsIds);
        }
      } catch (error) {
        console.log("No existing CAT results found");
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadExistingResults();
  }, [specialtyPropertyInfo.report_id]);

  const handleInitiateIcmRequest = async () => {
    setIsIcmInitiateLoading(true);
    try {
      const response = await initiateIcmRequest(
        specialtyPropertyInfo.report_id
      );
      setPendingIcmRequestIds([response.request_id]);

      // Refresh job history
      const history = await icmJobHistory(specialtyPropertyInfo.report_id);
      if (history.jobs) {
        setJobHistory((prev) => {
          const rmsJobs = prev.filter((j) => j.type === "rms");
          return [...(history.jobs ?? []), ...rmsJobs].sort(
            (a, b) =>
              new Date(b.started_at).getTime() -
              new Date(a.started_at).getTime()
          );
        });
      }
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "ICM Request Failed",
        detail: "There was an error initiating your ICM request.",
      });
    } finally {
      setIsIcmInitiateLoading(false);
    }
  };

  const handleInitiateRmsRequest = async () => {
    if (
      rmsLargeLocationPermitted &&
      numLocations > RMS_LARGE_LOCATION_THRESHOLD &&
      !showLargeLocationWarning
    ) {
      setShowLargeLocationWarning(true);
      return;
    }
    setShowLargeLocationWarning(false);
    setIsRmsInitiateLoading(true);
    try {
      const response = await initiateRmsRequest(
        specialtyPropertyInfo.report_id,
        rmsLargeLocationPermitted
      );
      setPendingRmsRequestIds([response.request_id]);

      // Refresh job history
      const history = await rmsJobHistory(specialtyPropertyInfo.report_id);
      if (history.jobs) {
        setJobHistory((prev) => {
          const icmJobs = prev.filter((j) => j.type === "icm");
          return [...icmJobs, ...(history.jobs ?? [])].sort(
            (a, b) =>
              new Date(b.started_at).getTime() -
              new Date(a.started_at).getTime()
          );
        });
      }
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "RMS Request Failed",
        detail: "There was an error initiating your RMS request.",
      });
    } finally {
      setIsRmsInitiateLoading(false);
    }
  };

  // Poll for ICM status
  useEffect(() => {
    if (pendingIcmRequestIds.length === 0) return;

    const checkStatus = async () => {
      try {
        const [statuses, history] = await Promise.all([
          Promise.all(
            pendingIcmRequestIds.map((id) =>
              icmRequestStatus(id).then((s) => ({ id, ...s }))
            )
          ),
          icmJobHistory(specialtyPropertyInfo.report_id),
        ]);

        if (history.jobs) {
          setJobHistory((prev) => {
            const rmsJobs = prev.filter((j) => j.type === "rms");
            return [...(history.jobs ?? []), ...rmsJobs].sort(
              (a, b) =>
                new Date(b.started_at).getTime() -
                new Date(a.started_at).getTime()
            );
          });
        }

        const completed = statuses.filter((s) => s.is_complete);
        if (completed.length === 0) return;

        const remaining = pendingIcmRequestIds.filter(
          (id) => !completed.some((c) => c.id === id)
        );

        const anyFailed = completed.some((s) => s.status === WorkStatus.FAILED);
        const allDone = remaining.length === 0;

        if (allDone) {
          const results = await catResults(specialtyPropertyInfo.report_id);
          setIcmResultsData(results);

          if (anyFailed) {
            toast.show({
              severity: "warn",
              summary: "ICM Requests Completed",
              detail:
                "Some ICM requests failed. Check run history for details.",
            });
          } else {
            toast.show({
              severity: "success",
              summary: "ICM Requests Complete",
              detail: "All ICM requests have been processed successfully.",
            });
          }
        }

        setPendingIcmRequestIds(remaining);
      } catch (error) {
        console.error("Failed to check ICM request status:", error);
      }
    };

    const intervalId = setInterval(checkStatus, 2000);
    return () => clearInterval(intervalId);
  }, [pendingIcmRequestIds, specialtyPropertyInfo.report_id, toast]);

  // Poll for RMS status
  useEffect(() => {
    if (pendingRmsRequestIds.length === 0) return;

    const checkStatus = async () => {
      try {
        const [statuses, history] = await Promise.all([
          Promise.all(
            pendingRmsRequestIds.map((id) =>
              rmsRequestStatus(id).then((s) => ({ id, ...s }))
            )
          ),
          rmsJobHistory(specialtyPropertyInfo.report_id),
        ]);

        if (history.jobs) {
          setJobHistory((prev) => {
            const icmJobs = prev.filter((j) => j.type === "icm");
            return [...icmJobs, ...(history.jobs ?? [])].sort(
              (a, b) =>
                new Date(b.started_at).getTime() -
                new Date(a.started_at).getTime()
            );
          });
        }

        const completed = statuses.filter((s) => s.is_complete);
        if (completed.length === 0) return;

        const remaining = pendingRmsRequestIds.filter(
          (id) => !completed.some((c) => c.id === id)
        );

        const anyFailed = completed.some((s) => s.status === WorkStatus.FAILED);
        const allDone = remaining.length === 0;

        if (allDone) {
          const results = await catResults(specialtyPropertyInfo.report_id);
          setRmsResultsData(results);

          if (anyFailed) {
            toast.show({
              severity: "warn",
              summary: "RMS Requests Completed",
              detail:
                "Some RMS requests failed. Check run history for details.",
            });
          } else {
            toast.show({
              severity: "success",
              summary: "RMS Requests Complete",
              detail: "All RMS requests have been processed successfully.",
            });
          }
        }

        setPendingRmsRequestIds(remaining);
      } catch (error) {
        console.error("Failed to check RMS request status:", error);
      }
    };

    const intervalId = setInterval(checkStatus, 2000);
    return () => clearInterval(intervalId);
  }, [pendingRmsRequestIds, specialtyPropertyInfo.report_id, toast]);

  const getPerilDisplayName = (perilCode: string): string => {
    const displayName = PERIL_DISPLAY_NAMES.get(perilCode) ?? perilCode;
    if (
      perilCode.toLowerCase() === "combined" ||
      displayName.toLowerCase() === "combined"
    ) {
      return "Layer Total";
    }
    return displayName;
  };

  const isLayerTotalPeril = (perilCode: string): boolean =>
    getPerilDisplayName(perilCode).toLowerCase() === "layer total";

  // Helper to extract RMS layers and match them to layer configs
  const getRmsLayers = useMemo((): RmsLayerResult[] => {
    const rmsData = rmsResultsData?.rms?.aggregate;
    if (!rmsData?.layers) return [];

    // For RMS, layer_guid already contains the report's actual layer ID
    return rmsData.layers
      .filter((layer) => {
        // Filter out invalid/empty layers
        return layer.layer_guid && layer.layer_guid !== "";
      })
      .map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid,
      }));
  }, [rmsResultsData]);

  const renderPerilLabel = (perilCode: string) => {
    if (!perilCode) {
      return null;
    }
    const label = getPerilDisplayName(perilCode);
    if (isLayerTotalPeril(perilCode)) {
      return <span className={styles.fontBold}>{label}</span>;
    }

    return (
      <span className={styles.perilLabel}>
        <span
          className={styles.perilColorDot}
          style={{ backgroundColor: getPerilColor(perilCode) }}
        />
        <span>{label}</span>
      </span>
    );
  };

  const layerInfoById = useMemo(
    () =>
      new Map(
        specialtyPropertyInfo.report_json.layer_info.map((layer) => [
          layer.layer_id!,
          layer,
        ])
      ),
    [specialtyPropertyInfo]
  );

  // Get losses grouped by layer, with per-peril rows within each layer
  const lossesByLayerPeril = useMemo((): LayerPerilRow[] => {
    if (selectedModel === "icm") {
      const icmAir = icmResultsData?.icm?.air;
      const outerLayers = icmAir?.aggregate?.layers || [];
      if (outerLayers.length === 0) return [];

      // Filter out invalid/empty layers - layer_guid IS the actual layer_id
      const validLayers = outerLayers.filter((layer) => {
        // Filter out empty layers with no layer_guid
        return layer.layer_guid && layer.layer_guid !== "";
      });

      const enrichedLayers = validLayers.map((layer) => ({
        ...layer,
        layer_id: layer.layer_guid ?? null,
      }));

      const sortedLayers = [...enrichedLayers].sort((a, b) => {
        const infoA = a.layer_id ? layerInfoById.get(a.layer_id) : undefined;
        const infoB = b.layer_id ? layerInfoById.get(b.layer_id) : undefined;
        const topA = infoA ? infoA.starting_point + infoA.range : Infinity;
        const topB = infoB ? infoB.starting_point + infoB.range : Infinity;
        return topA - topB;
      });

      const rows: LayerPerilRow[] = [];

      for (const layer of sortedLayers) {
        const layerInfo = layer.layer_id
          ? layerInfoById.get(layer.layer_id)
          : undefined;
        const layerName = layerInfo
          ? readableLayer(layerInfo)
          : (layer.layer_id ?? "Unknown");

        const perilEntries = Object.entries(layer.perils || {}).sort(
          ([a], [b]) =>
            getPerilDisplayName(a).localeCompare(getPerilDisplayName(b))
        );

        let isFirst = true;
        for (const [peril, data] of perilEntries) {
          rows.push({
            layer: layerName,
            peril,
            aal: data.aal ?? null,
            pml_100: data.pml_per_period?.["100"] ?? null,
            pml_250: data.pml_per_period?.["250"] ?? null,
            pml_1000: data.pml_per_period?.["1000"] ?? null,
            isFirstInGroup: isFirst,
          });
          isFirst = false;
        }

        rows.push({
          layer: layerName,
          peril: "Combined",
          aal: layer.totals?.aal ?? null,
          pml_100: layer.totals?.pml_per_period?.["100"] ?? null,
          pml_250: layer.totals?.pml_per_period?.["250"] ?? null,
          pml_1000: layer.totals?.pml_per_period?.["1000"] ?? null,
          isFirstInGroup: isFirst,
        });
      }

      return rows;
    } else {
      // RMS model - use nested layers from aggregate_results
      const rmsLayers = getRmsLayers;
      if (rmsLayers.length === 0) return [];

      const sortedLayers = [...rmsLayers].sort((a, b) => {
        const infoA = a.layer_id ? layerInfoById.get(a.layer_id) : undefined;
        const infoB = b.layer_id ? layerInfoById.get(b.layer_id) : undefined;
        const topA = infoA ? infoA.starting_point + infoA.range : Infinity;
        const topB = infoB ? infoB.starting_point + infoB.range : Infinity;
        return topA - topB;
      });

      const rows: LayerPerilRow[] = [];

      for (const rmsLayer of sortedLayers) {
        const layerInfo = rmsLayer.layer_id
          ? layerInfoById.get(rmsLayer.layer_id)
          : undefined;
        const layerName = layerInfo
          ? readableLayer(layerInfo)
          : (rmsLayer.layer_id ?? "Unknown");

        // RMS perils are in the perils object, but they may not have AAL values
        // For now, just show the totals
        const perilEntries = Object.entries(rmsLayer.perils || {}).sort(
          ([a], [b]) =>
            getPerilDisplayName(a).localeCompare(getPerilDisplayName(b))
        );

        // Get EP curves for this layer for PML values
        const layerEpCurves = rmsLayer.ep_curves || [];
        const grAepCurve = layerEpCurves.find(
          (c: client.EpRow) => c.perspective === "GR" && c.metric_type === "AEP"
        );
        const pml100 = grAepCurve?.return_period_values?.["100"] ?? null;
        const pml250 = grAepCurve?.return_period_values?.["250"] ?? null;
        const pml1000 = grAepCurve?.return_period_values?.["1000"] ?? null;

        let isFirst = true;
        for (const [peril, data] of perilEntries) {
          const perilData = data as client.CatPerilLayerData;
          rows.push({
            layer: layerName,
            peril,
            aal: perilData.aal ?? null,
            pml_100: perilData.pml_per_period?.["100"] ?? null,
            pml_250: perilData.pml_per_period?.["250"] ?? null,
            pml_1000: perilData.pml_per_period?.["1000"] ?? null,
            isFirstInGroup: isFirst,
          });
          isFirst = false;
        }

        // Add combined row from totals
        rows.push({
          layer: layerName,
          peril: "Combined",
          aal: rmsLayer.totals?.aal ?? null,
          pml_100: pml100,
          pml_250: pml250,
          pml_1000: pml1000,
          isFirstInGroup: isFirst,
        });
      }

      return rows;
    }
  }, [
    selectedModel,
    icmResultsData,
    rmsResultsData,
    layerInfoById,
    getRmsLayers,
  ]);

  // RMS EP Curve data
  const epRows = useMemo((): EPTableRow[] => {
    // Get aggregate EP curves from RMS results
    const rmsAggregate = rmsResultsData?.rms?.aggregate;
    const epCurves = rmsAggregate?.ep_curves;
    if (!epCurves) return [];

    const rows: EPTableRow[] = (epCurves as RmsEpRowWithPeril[]).map(
      (curve) => {
        const rpValues: Record<number, number> = {};
        for (const rp of RETURN_PERIODS) {
          rpValues[rp] =
            curve.return_period_values?.[String(rp)] ??
            curve.return_period_values?.[`${rp}.0`] ??
            0;
        }
        const perilLabel = curve.peril ? ` ${curve.peril}` : "";
        return {
          label: `${curve.perspective} ${curve.metric_type}${perilLabel}`,
          perspective: curve.perspective,
          metric_type: curve.metric_type,
          values: rpValues,
        };
      }
    );

    const perspectiveOrder: Record<string, number> = {
      GU: 0,
      GR: 1,
      RL: 2,
    };
    const metricOrder: Record<string, number> = { AEP: 0, OEP: 1 };
    rows.sort((a, b) => {
      const pDiff =
        (perspectiveOrder[a.perspective] ?? 99) -
        (perspectiveOrder[b.perspective] ?? 99);
      if (pDiff !== 0) return pDiff;
      return (
        (metricOrder[a.metric_type] ?? 99) - (metricOrder[b.metric_type] ?? 99)
      );
    });

    rows.push({
      label: "Total AAL (GU)",
      perspective: "",
      metric_type: "",
      values: {},
      isTotalRow: true,
    });

    return rows;
  }, [rmsResultsData]);

  // Filter job history based on selected model
  const filteredJobHistory = useMemo(
    () => jobHistory.filter((job) => job.type === selectedModel),
    [jobHistory, selectedModel]
  );

  const hasIcmResults =
    (icmResultsData?.icm?.air?.aggregate?.layers?.length ?? 0) > 0;
  const hasRmsResults =
    (rmsResultsData?.rms?.aggregate?.layers?.length ?? 0) > 0;
  const hasResults = hasIcmResults || hasRmsResults;

  const handleViewJobResults = async (job: CatJobHistoryItem) => {
    try {
      const results = await catResults(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      if (job.type === "icm") {
        setIcmResultsData(results);
      } else {
        setRmsResultsData(results);
      }
      setViewingBatchId(job.batch_id);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load results for this job.",
      });
    }
  };

  const handleResetToDefaultResults = async () => {
    try {
      const results = await catResults(specialtyPropertyInfo.report_id);
      setIcmResultsData(results);
      setRmsResultsData(results);
      setViewingBatchId(null);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load default results.",
      });
    }
  };

  const handleDownloadArtifact = async (
    job: CatJobHistoryItem,
    artifact: "analyses" | "request" | "error_response"
  ) => {
    try {
      const { data, filename } =
        job.type === "icm"
          ? await downloadIcmArtifact(
              specialtyPropertyInfo.report_id,
              job.batch_id,
              artifact
            )
          : await downloadRmsArtifact(
              specialtyPropertyInfo.report_id,
              job.batch_id,
              artifact
            );
      downloadBlob(filename ?? `${artifact}_${job.batch_id}`, data);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Could not download the requested file.",
      });
    }
  };

  const handleDownloadAll = async (job: CatJobHistoryItem) => {
    try {
      const { data, filename } =
        job.type === "icm"
          ? await downloadAllIcmArtifacts(
              specialtyPropertyInfo.report_id,
              job.batch_id
            )
          : await downloadAllRmsArtifacts(
              specialtyPropertyInfo.report_id,
              job.batch_id
            );
      downloadBlob(filename ?? `${job.type}_all_${job.batch_id}.zip`, data);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Could not download the artifacts zip.",
      });
    }
  };

  const renderStatusCell = (job: CatJobHistoryItem) => {
    const modelTag = (
      <Tag
        value={job.type.toUpperCase()}
        severity={job.type === "icm" ? "info" : "warning"}
        className={styles.modelTag}
      />
    );

    const isRunning =
      job.status === WorkStatus.PENDING ||
      job.status === WorkStatus.IN_PROGRESS;

    if (isRunning) {
      return (
        <div className={styles.statusRunning}>
          <ProgressSpinner className={styles.spinnerSmall} strokeWidth="4" />
          <span>In Progress</span>
          {modelTag}
        </div>
      );
    }

    switch (job.status) {
      case WorkStatus.DONE:
        return (
          <span className={styles.statusCompleted}>Completed {modelTag}</span>
        );
      case WorkStatus.FAILED:
        return <span className={styles.statusFailed}>Failed {modelTag}</span>;
      default:
        return (
          <span>
            {job.status} {modelTag}
          </span>
        );
    }
  };

  const isIcmPending = pendingIcmRequestIds.length > 0 || isIcmInitiateLoading;
  const isRmsPending = pendingRmsRequestIds.length > 0 || isRmsInitiateLoading;
  const isPending = isIcmPending || isRmsPending;

  const pendingMessage = (() => {
    if (isIcmPending && isRmsPending) {
      return "ICM and RMS analyses in progress...";
    } else if (isIcmPending) {
      return "ICM analysis in progress...";
    } else if (isRmsPending) {
      return "RMS analysis in progress...";
    }
    return "";
  })();

  return (
    <Card title="Catastrophe Modeling (ICM & RMS)" className={styles.catCard}>
      <div className={styles.content}>
        {isPending && (
          <Message
            severity="info"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <ProgressSpinner
                  className={styles.spinnerMedium}
                  strokeWidth="4"
                />
                <span>{pendingMessage}</span>
              </div>
            }
          />
        )}

        {viewingBatchId && (
          <Message
            severity="warn"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <span>
                  Viewing results from a historical run — these are not the
                  active results.
                </span>
                <Button
                  label="Reset to default"
                  text
                  size="small"
                  onClick={handleResetToDefaultResults}
                />
              </div>
            }
          />
        )}

        <div style={{ marginBottom: "16px" }}>
          <SelectButton
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.value)}
            options={[
              { label: "RMS", value: "rms" },
              { label: "ICM", value: "icm" },
            ]}
          />
        </div>
        <div
          className={styles.buttonContainer}
          style={{ marginBottom: "16px" }}
        >
          {selectedModel === "icm" ? (
            <IfFeatureEnabled feature="show-icm-start-button" user={localUser}>
              <Button
                label={hasIcmResults ? "Re-run ICM" : "Run ICM"}
                icon="pi pi-play"
                onClick={handleInitiateIcmRequest}
                loading={isIcmInitiateLoading}
                disabled={isIcmPending}
              />
            </IfFeatureEnabled>
          ) : (
            <span
              title={
                !rmsLargeLocationPermitted &&
                numLocations > RMS_LARGE_LOCATION_THRESHOLD
                  ? `This report has ${numLocations.toLocaleString()} locations, which exceeds the ${RMS_LARGE_LOCATION_THRESHOLD.toLocaleString()} location limit for RMS.`
                  : undefined
              }
            >
              <Button
                label={hasRmsResults ? "Re-run RMS" : "Run RMS"}
                icon="pi pi-play"
                onClick={handleInitiateRmsRequest}
                loading={isRmsInitiateLoading}
                disabled={
                  isRmsPending ||
                  (!rmsLargeLocationPermitted &&
                    numLocations > RMS_LARGE_LOCATION_THRESHOLD)
                }
              />
            </span>
          )}
        </div>

        {hasResults || jobHistory.length > 0 ? (
          <div className={styles.resultsContainer}>
            <TabView>
              <TabPanel header="By Layer and Peril">
                {lossesByLayerPeril.length > 0 ? (
                  <DataTable
                    value={lossesByLayerPeril}
                    rowGroupMode="rowspan"
                    groupRowsBy="layer"
                    rowClassName={(rowData: LayerPerilRow) => {
                      const classes: string[] = [];
                      if (isLayerTotalPeril(rowData.peril)) {
                        classes.push(styles.layerTotalRow);
                      }
                      if (rowData.isFirstInGroup) {
                        classes.push(styles.layerGroupStart);
                      }
                      return classes.join(" ");
                    }}
                  >
                    <Column
                      field="layer"
                      header="Layer"
                      style={{ minWidth: "100px" }}
                      bodyClassName={styles.layerCell}
                    />
                    <Column
                      field="peril"
                      header="Peril"
                      body={(rowData: LayerPerilRow) =>
                        renderPerilLabel(rowData.peril)
                      }
                      style={{ minWidth: "100px" }}
                    />
                    <Column
                      field="aal"
                      header="AAL"
                      body={(rowData: LayerPerilRow) =>
                        rowData.aal != null
                          ? formatExactMoneyDollars(rowData.aal, {
                              withRounding: true,
                              withDollarSign: true,
                            })
                          : ""
                      }
                      style={{ minWidth: "120px" }}
                      align="right"
                    />
                    <Column
                      field="pml_100"
                      header="100 Year PML"
                      body={(rowData: LayerPerilRow) =>
                        rowData.pml_100 != null
                          ? formatExactMoneyDollars(rowData.pml_100, {
                              withRounding: true,
                              withDollarSign: true,
                            })
                          : ""
                      }
                      style={{ minWidth: "120px" }}
                      align="right"
                    />
                    <Column
                      field="pml_250"
                      header="250 Year PML"
                      body={(rowData: LayerPerilRow) =>
                        rowData.pml_250 != null
                          ? formatExactMoneyDollars(rowData.pml_250, {
                              withRounding: true,
                              withDollarSign: true,
                            })
                          : ""
                      }
                      style={{ minWidth: "120px" }}
                      align="right"
                    />
                    <Column
                      field="pml_1000"
                      header="1000 Year PML"
                      body={(rowData: LayerPerilRow) =>
                        rowData.pml_1000 != null
                          ? formatExactMoneyDollars(rowData.pml_1000, {
                              withRounding: true,
                              withDollarSign: true,
                            })
                          : ""
                      }
                      style={{ minWidth: "120px" }}
                      align="right"
                    />
                  </DataTable>
                ) : (
                  <Message
                    severity="info"
                    text={
                      selectedModel === "icm"
                        ? "No ICM data available. Run an ICM analysis to generate results."
                        : "No RMS data available. Run an RMS analysis to generate results."
                    }
                    className={styles.messageMargin}
                  />
                )}
              </TabPanel>

              {selectedModel === "rms" && (
                <TabPanel header="EP Curves">
                  {epRows.length > 0 ? (
                    <DataTable
                      value={epRows}
                      rowClassName={(rowData: EPTableRow) =>
                        rowData.isTotalRow ? styles.layerTotalRow : ""
                      }
                    >
                      <Column
                        field="label"
                        header="Metric"
                        style={{ minWidth: "150px" }}
                        bodyClassName={(rowData: EPTableRow) =>
                          rowData.isTotalRow ? styles.fontBold : ""
                        }
                      />
                      {RETURN_PERIODS.map((rp) => (
                        <Column
                          key={rp}
                          header={`${rp}y`}
                          body={(rowData: EPTableRow) => {
                            if (rowData.isTotalRow) {
                              const totalAal =
                                rmsResultsData?.rms?.aggregate
                                  ?.aal_combined_groundup ?? null;
                              return totalAal
                                ? formatExactMoneyDollars(totalAal, {
                                    withRounding: true,
                                    withDollarSign: true,
                                  })
                                : "";
                            }
                            return rowData.values[rp]
                              ? formatExactMoneyDollars(rowData.values[rp], {
                                  withRounding: true,
                                  withDollarSign: true,
                                })
                              : "";
                          }}
                          style={{ minWidth: "100px" }}
                          align="right"
                        />
                      ))}
                    </DataTable>
                  ) : (
                    <Message
                      severity="info"
                      text="No RMS EP curve data available. Run an RMS analysis to generate results."
                      className={styles.messageMargin}
                    />
                  )}
                </TabPanel>
              )}

              <TabPanel header="Run History">
                {filteredJobHistory.length > 0 ? (
                  <DataTable
                    value={filteredJobHistory}
                    sortField="started_at"
                    sortOrder={-1}
                  >
                    <Column
                      field="status"
                      header="Status"
                      body={renderStatusCell}
                      style={{ minWidth: "160px" }}
                      align="left"
                    />
                    <Column
                      field="started_at"
                      header="Started"
                      sortable
                      body={(job: CatJobHistoryItem) =>
                        new Date(job.started_at).toLocaleString()
                      }
                      style={{ minWidth: "160px" }}
                      align="right"
                    />
                    <Column
                      header="Duration"
                      body={(job: CatJobHistoryItem) =>
                        job.status === WorkStatus.DONE ||
                        job.status === WorkStatus.FAILED
                          ? formatDuration(job.started_at, job.finished_at)
                          : "—"
                      }
                      style={{ minWidth: "100px" }}
                      align="right"
                    />
                    <Column
                      field="num_addresses"
                      header="Addresses"
                      body={(job: CatJobHistoryItem) =>
                        job.num_addresses?.toLocaleString() ?? "—"
                      }
                      style={{ minWidth: "100px" }}
                      align="right"
                    />
                    <Column
                      field="total_tiv"
                      header="Total TIV"
                      body={(job: CatJobHistoryItem) =>
                        job.total_tiv
                          ? formatExactMoneyDollars(job.total_tiv, {
                              withRounding: true,
                              withDollarSign: true,
                            })
                          : "—"
                      }
                      style={{ minWidth: "120px" }}
                      align="right"
                    />
                    <Column
                      field="total_aal"
                      header="Total AAL"
                      body={(job: CatJobHistoryItem) =>
                        job.total_aal
                          ? formatExactMoneyDollars(job.total_aal, {
                              withRounding: true,
                              withDollarSign: true,
                            })
                          : "—"
                      }
                      style={{ minWidth: "120px" }}
                      align="right"
                    />
                    <Column
                      header=""
                      body={(job: CatJobHistoryItem) => {
                        if (job.status === WorkStatus.DONE) {
                          return (
                            <div className={styles.actionButtons}>
                              <Button
                                icon="pi pi-eye"
                                text
                                rounded
                                severity={
                                  viewingBatchId === job.batch_id
                                    ? "info"
                                    : "secondary"
                                }
                                tooltip="View these results"
                                tooltipOptions={{ position: "left" }}
                                onClick={() => handleViewJobResults(job)}
                                disabled={viewingBatchId === job.batch_id}
                              />
                              <Button
                                icon="pi pi-download"
                                text
                                rounded
                                severity="secondary"
                                tooltip="Download all artifacts"
                                tooltipOptions={{ position: "left" }}
                                onClick={() => handleDownloadAll(job)}
                              />
                            </div>
                          );
                        }
                        if (job.status === WorkStatus.FAILED) {
                          return (
                            <div className={styles.actionButtons}>
                              <Button
                                icon="pi pi-file-export"
                                text
                                rounded
                                severity="secondary"
                                tooltip="Download request"
                                tooltipOptions={{ position: "left" }}
                                onClick={() =>
                                  handleDownloadArtifact(job, "request")
                                }
                              />
                              <Button
                                icon="pi pi-download"
                                text
                                rounded
                                severity="secondary"
                                tooltip="Download error response"
                                tooltipOptions={{ position: "left" }}
                                onClick={() =>
                                  handleDownloadArtifact(job, "error_response")
                                }
                              />
                            </div>
                          );
                        }
                        return null;
                      }}
                      style={{ width: "100px" }}
                      align="center"
                    />
                  </DataTable>
                ) : (
                  <Message
                    severity="info"
                    text="No job history available"
                    className={styles.messageMargin}
                  />
                )}
              </TabPanel>
            </TabView>
          </div>
        ) : isInitialLoading ? (
          <div className={styles.loadingContainer}>
            <ProgressSpinner />
          </div>
        ) : !isPending ? (
          <Message
            severity="info"
            text="No CAT results available yet. Run an ICM or RMS analysis to generate results."
            className={styles.messageMargin}
          />
        ) : null}
      </div>

      {/* Large Location Warning Dialog */}
      <Dialog
        header="Large Location Warning"
        visible={showLargeLocationWarning}
        onHide={() => setShowLargeLocationWarning(false)}
        draggable={false}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={() => setShowLargeLocationWarning(false)}
            />
            <Button
              label="Proceed"
              icon="pi pi-play"
              severity="warning"
              onClick={handleInitiateRmsRequest}
              loading={isRmsInitiateLoading}
            />
          </div>
        }
      >
        <p>
          This report has <b>{numLocations.toLocaleString()}</b> locations,
          which exceeds the typical limit of{" "}
          {RMS_LARGE_LOCATION_THRESHOLD.toLocaleString()}. This RMS request may
          be expensive to run!
        </p>
      </Dialog>
    </Card>
  );
};
